import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, RefreshCw } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import { RecordDetailCard, RecordFieldRow } from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { ClientPrimaryTabs } from "../components/PortalNavigation";
import { PortalLink } from "../components/PortalLink";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { usePortalData } from "../hooks/usePortalData";
import { useSessionLocation } from "../hooks/useSessionLocation";
import {
  navigateWithinPortal,
  withPortalContext,
} from "../routing/portalRoutes";
import {
  clientSessionPath,
  SESSION_PAGE_SIZE,
} from "../utils/clientSessionNavigation";
import { formatPortalDateTime } from "../utils/dateTime";

function senderLabel(sender: string) {
  const normalized = sender.trim().toLowerCase();
  if (normalized === "user") return "Client";
  if (normalized === "assistant") return "Assistant";
  return sender.replace(/_/g, " ");
}

export function ClientSessionTranscriptPage({
  clinicianCode,
  patientId,
  sessionId,
}: {
  clinicianCode: string;
  patientId: string;
  sessionId: string;
}) {
  const workspace = useClientWorkspace();
  const { search, messageOffset } = useSessionLocation();
  const backPath = clientSessionPath(patientId, workspace.source, search);
  const [copyStatus, setCopyStatus] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    setCopyStatus("");
  }, [sessionId]);
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getClientSessionTranscript(
        clinicianCode,
        patientId,
        sessionId,
        SESSION_PAGE_SIZE,
        messageOffset,
        signal,
        workspace.accessScope,
      ),
    [clinicianCode, patientId, sessionId, messageOffset, workspace.accessScope],
  );
  const state = usePortalData(request);
  const response =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data =
    response?.patient.id === patientId &&
    response.sessionId === sessionId &&
    response.pageOffset === messageOffset &&
    response.pageSize === SESSION_PAGE_SIZE
      ? response
      : undefined;
  const snapshot =
    state.status === "error"
      ? undefined
      : getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const patient = data?.patient ?? snapshot?.patient;
  const therapist = data?.therapist ?? snapshot?.therapist;
  const pending = state.status === "loading" || state.status === "refreshing";
  const heading = useRef<HTMLHeadingElement>(null);
  const focusPage = useRef<number | null>(null);
  useEffect(() => {
    if (
      data &&
      !pending &&
      focusPage.current === data.pageOffset &&
      heading.current
    ) {
      heading.current.focus();
      focusPage.current = null;
    }
  }, [data, pending]);
  const changePage = (offset: number) => {
    focusPage.current = offset;
    navigateWithinPortal(
      withPortalContext(
        clientSessionPath(
          patientId,
          workspace.source,
          search,
          sessionId,
          offset,
        ),
      ),
    );
  };
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(sessionId);
      if (mounted.current) setCopyStatus("Session ID copied.");
    } catch {
      if (mounted.current)
        setCopyStatus(
          "Unable to copy. Select and copy the Session ID instead.",
        );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client",
              "Session Transcript",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="sessions"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-session-transcript">
        <div className="cp-content-header">
          <PortalLink path={backPath} clinicianCode={clinicianCode}>
            ← Back to Sessions
          </PortalLink>
          <button
            type="button"
            className="cp-button cp-button--secondary"
            onClick={state.refresh}
            disabled={pending}
            aria-label={
              pending ? "Refreshing transcript" : "Refresh transcript"
            }
          >
            <RefreshCw aria-hidden="true" /> Refresh
          </button>
        </div>
        {pending && !data ? <LoadingState label="Loading transcript" /> : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {state.status === "success" && !data ? (
          <ErrorState error={new Error("Transcript is unavailable")} />
        ) : null}
        {data ? (
          <section
            aria-labelledby="session-transcript-title"
            aria-busy={pending}
          >
            <h2 id="session-transcript-title" ref={heading} tabIndex={-1}>
              Session Transcript
            </h2>
            <RecordDetailCard>
              <RecordFieldRow label="Start Time">
                {formatPortalDateTime(data.session.startedAt)}
              </RecordFieldRow>
              <RecordFieldRow label="Last Message Time">
                {formatPortalDateTime(data.session.lastMessageAt)}
              </RecordFieldRow>
              <RecordFieldRow label="Message Count">
                {data.session.messageCount.toLocaleString()}
              </RecordFieldRow>
            </RecordDetailCard>
            <details className="cp-session-transcript__details">
              <summary>Session details</summary>
              <div className="cp-session-transcript__identity">
                <span>
                  Session ID: <code>{sessionId}</code>
                </span>
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  onClick={copyId}
                >
                  <Copy aria-hidden="true" /> Copy Session ID
                </button>
              </div>
              <p role="status">{copyStatus}</p>
            </details>
            {data.messages.length ? (
              <ol
                className="cp-session-transcript__messages"
                aria-label="Conversation messages"
                start={messageOffset + 1}
              >
                {data.messages.map((message) => (
                  <li key={message.id}>
                    <article
                      className="cp-session-transcript__message"
                      data-sender={
                        message.sender.toLowerCase() === "user"
                          ? "client"
                          : "other"
                      }
                    >
                      <header>
                        <strong>{senderLabel(message.sender)}</strong>
                        <time dateTime={message.sentAt}>
                          {formatPortalDateTime(message.sentAt, {
                            second: "2-digit",
                          })}
                        </time>
                      </header>
                      <p>
                        {message.message === null || message.message === "" ? (
                          <em>No text content recorded.</em>
                        ) : (
                          message.message
                        )}
                      </p>
                    </article>
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyState
                title="No messages on this page"
                description="Return to a previous page to read this conversation."
              />
            )}
            <nav className="cp-pagination" aria-label="Transcript pagination">
              <span className="cp-pagination__summary" role="status">
                {data.messages.length
                  ? `Messages ${messageOffset + 1}–${messageOffset + data.messages.length} of ${data.session.messageCount}`
                  : "0 messages on this page"}
              </span>
              <div className="cp-pagination__controls">
                <button
                  type="button"
                  className="cp-pagination__button"
                  aria-label="Previous messages"
                  disabled={pending || messageOffset === 0}
                  onClick={() =>
                    changePage(Math.max(0, messageOffset - SESSION_PAGE_SIZE))
                  }
                >
                  <ChevronLeft aria-hidden="true" />
                </button>
                <span className="cp-pagination__page-status">
                  Page {messageOffset / SESSION_PAGE_SIZE + 1}
                </span>
                <button
                  type="button"
                  className="cp-pagination__button"
                  aria-label="Next messages"
                  disabled={
                    pending || !data.hasMore || data.nextOffset === null
                  }
                  onClick={() => {
                    if (data.nextOffset !== null) changePage(data.nextOffset);
                  }}
                >
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </nav>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
